import { isKnownAccountDeletionSsoProvider } from "../constants/accountDeletionSsoProviders";

const CALLBACK_STORAGE_KEY = "muzone.accountDeletion.ssoCallback.v1";
const CALLBACK_MAX_AGE_MS = 5 * 60 * 1000;

export const ACCOUNT_DELETION_SSO_FLOWS = Object.freeze({
    active: "active",
    restricted: "restricted",
    recovery: "recovery",
});

const ALLOWED_FLOWS = new Set(Object.values(ACCOUNT_DELETION_SSO_FLOWS));

const requireProvider = (value) => {
    const provider = typeof value === "string" ? value.trim().toLowerCase() : "";
    if (!isKnownAccountDeletionSsoProvider(provider)) {
        throw new TypeError("Account-deletion SSO provider is unsupported");
    }
    return provider;
};

const requireFlow = (value) => {
    if (!ALLOWED_FLOWS.has(value)) {
        throw new TypeError("Account-deletion SSO flow is unsupported");
    }
    return value;
};

const getSessionStorage = () => {
    if (typeof window === "undefined") {
        throw new Error("Per-tab SSO callback storage is unavailable");
    }
    try {
        if (!window.sessionStorage) {
            throw new Error("Per-tab SSO callback storage is unavailable");
        }
        return window.sessionStorage;
    } catch (_) {
        throw new Error("Per-tab SSO callback storage is unavailable");
    }
};

export const rememberAccountDeletionSsoCallback = ({ flow, provider }) => {
    const descriptor = {
        flow: requireFlow(flow),
        provider: requireProvider(provider),
        startedAt: Date.now(),
    };
    const storage = getSessionStorage();
    storage.setItem(CALLBACK_STORAGE_KEY, JSON.stringify(descriptor));

    // Fail before redirecting if the browser cannot round-trip the descriptor.
    const persisted = JSON.parse(storage.getItem(CALLBACK_STORAGE_KEY) || "null");
    if (persisted?.flow !== descriptor.flow || persisted?.provider !== descriptor.provider) {
        storage.removeItem(CALLBACK_STORAGE_KEY);
        throw new Error("Per-tab SSO callback storage is unavailable");
    }
};

export const clearAccountDeletionSsoCallback = () => {
    try {
        window.sessionStorage?.removeItem(CALLBACK_STORAGE_KEY);
    } catch (_) {
        // The descriptor contains no credential; a failed cleanup is handled by expiry validation.
    }
};

const consumeDescriptor = () => {
    let raw = null;
    try {
        const storage = getSessionStorage();
        raw = storage.getItem(CALLBACK_STORAGE_KEY);
        storage.removeItem(CALLBACK_STORAGE_KEY);
    } catch (_) {
        return null;
    }
    if (!raw) {
        return null;
    }
    try {
        const descriptor = JSON.parse(raw);
        const age = Date.now() - descriptor?.startedAt;
        if (!ALLOWED_FLOWS.has(descriptor?.flow)
            || !isKnownAccountDeletionSsoProvider(descriptor?.provider)
            || !Number.isFinite(age)
            || age < 0
            || age > CALLBACK_MAX_AGE_MS) {
            return null;
        }
        return { flow: descriptor.flow, provider: descriptor.provider };
    } catch (_) {
        return null;
    }
};

export const readAccountDeletionSsoCallback = () => {
    if (typeof window === "undefined") {
        return null;
    }
    const query = new URLSearchParams(window.location.search);
    const code = query.get("code");
    const state = query.get("state");
    const providerError = query.get("error");
    const descriptor = consumeDescriptor();
    if (!code && !state && !providerError) {
        return null;
    }
    return descriptor
        ? { ...descriptor, code, state, providerError }
        : { code, state, providerError, flow: null, provider: null };
};

export const requireSafeAccountDeletionAuthorizeUrl = (value) => {
    const authorizeUrl = new URL(value);
    const loopback = new Set(["localhost", "127.0.0.1", "::1", "[::1]"])
        .has(authorizeUrl.hostname.toLowerCase());
    if ((authorizeUrl.protocol !== "https:" && !(authorizeUrl.protocol === "http:" && loopback))
        || authorizeUrl.username
        || authorizeUrl.password) {
        throw new TypeError("Unsafe SSO authorization URL");
    }
    return authorizeUrl.toString();
};

export const navigateToAccountDeletionSsoProvider = (authorizeUrl) => {
    window.location.assign(authorizeUrl);
};

export const ACCOUNT_DELETION_SSO_CALLBACK_STORAGE_KEY = CALLBACK_STORAGE_KEY;
